import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AttendanceManagement from './pages/AttendanceManagement';
import ClassManagement from './pages/ClassManagement';
import UserManagement from './pages/UserManagement';
import Reports from './pages/Reports';
import AttendanceRegister from './pages/AttendanceRegister';
import StudentAttendance from './pages/StudentAttendance';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router } from 'react-router-dom';
import CourseStructureManagement from './pages/CourseStructureManagement';
import ClassroomManagement from './pages/ClassroomManagement';
import AttendanceRegisterQR from './pages/AttendanceRegisterQR';
import AttendanceSuccess from './pages/AttendanceSuccess';
import IntegratedClassManagement from './pages/IntegratedClassManagement';
import StudentManagement from './pages/StudentManagement';

// 보호된 라우트 컴포넌트
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  console.log('ProtectedRoute - auth info:', { 
    isAuthenticated, 
    user: user ? { 
      id: user.id, 
      username: user.username, 
      roles: user.roles 
    } : null, 
    requiredRoles, 
    loading 
  });
  
  // 로딩 중인 경우 로딩 표시
  if (loading) {
    console.log('인증 정보 로딩 중...');
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    console.log('회원가 인증되지 않았습니다. 로그인 페이지로 리디렉션합니다.');
    return <Navigate to="/login" />;
  }
  
  // 역할 요구 사항이 있는 경우 확인
  if (requiredRoles.length > 0) {
    // 회원 역할이 없는 경우 처리
    if (!user?.roles || !Array.isArray(user.roles)) {
      console.error('회원 역할 정보가 없거나 올바른 형식이 아닙니다:', user);
      return <Navigate to="/dashboard" />;
    }
    
    // 역할 검사
    const hasRequiredRole = requiredRoles.some(role => 
      user.roles.includes(role)
    );
    
    console.log('역할 확인 결과:', { 
      userRoles: user.roles, 
      requiredRoles, 
      hasRequiredRole,
      hasAdminRole: user.roles.includes('admin')
    });
    
    // 디버깅 용도로 admin 역할이 있으면 항상 접근 허용
    if (user.roles.includes('admin')) {
      console.log('관리자(admin) 역할이 확인되어 접근을 허용합니다.');
      return children;
    }
    
    if (!hasRequiredRole) {
      console.log('회원에게 필요한 역할이 없습니다. 대시보드로 리디렉션합니다.');
      return <Navigate to="/dashboard" />;
    }
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/student-attendance/:classId" element={<StudentAttendance />} />
        <Route path="/attendance-register/:classId" element={<AttendanceRegisterQR />} />
        <Route path="/attendance/register" element={<AttendanceRegisterQR />} />
        <Route path="/attendance-success" element={<AttendanceSuccess />} />
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="attendance" element={<AttendanceManagement />} />
          <Route path="attendance-register" element={<AttendanceRegister />} />
          <Route path="classes" element={
            <ProtectedRoute requiredRoles={['instructor', 'admin']}>
              <ClassManagement />
            </ProtectedRoute>
          } />
          <Route path="integrated-class-management" element={
            <ProtectedRoute requiredRoles={['admin', 'instructor']}>
              <IntegratedClassManagement />
            </ProtectedRoute>
          } />
          <Route path="classrooms" element={
            <ProtectedRoute requiredRoles={['admin', 'instructor']}>
              <ClassroomManagement />
            </ProtectedRoute>
          } />
          <Route path="users" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="reports" element={
            <ProtectedRoute requiredRoles={['instructor', 'admin']}>
              <Reports />
            </ProtectedRoute>
          } />
          <Route
            path="/course-structure"
            element={
              <ProtectedRoute requiredRoles={['admin', 'instructor']}>
                <CourseStructureManagement />
              </ProtectedRoute>
            }
          />
          <Route path="students" element={<ProtectedRoute><StudentManagement /></ProtectedRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App; 